import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Calendar } from 'primereact/calendar';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import toastService from '../../../../../utils/toastService';
import { formatErrorMessage } from '../../../../../utils/errorUtils';
import { useGetLmsNewsByIdQuery } from '../../../../../api';
import NewsPhotoPicker from '../../components/NewsPhotoPicker';
import useLmsNewsSave from '../../hooks/useLmsNewsSave';
import { EMPTY_PHOTO_STATE, NEWS_SHORT_DESCRIPTION_MAX, NEWS_TITLE_MAX, NEWS_TYPE, formatFee, formatTrainingDateRange, isAnnouncement, parseUtcDate, toApiUtcDate } from '../../utils/newsUtils';

const EMPTY_FORM = { title: '', shortDescription: '', detailDescription: '', publishDate: null };

const ReadonlyItem = ({ label, value }) => (
    <div className="col-12 md:col-6 flex flex-column gap-1">
        <span className="text-xs text-color-secondary">{label}</span>
        <span className="font-medium">{value || '-'}</span>
    </div>
);

const LmsNewsFormDialog = ({ visible, newsId, onHide, onSaved, readOnly = false }) => {
    const { th } = useLanguage();
    const [currentId, setCurrentId] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [photo, setPhoto] = useState(EMPTY_PHOTO_STATE);
    const [submitted, setSubmitted] = useState(false);
    const { saveNews, isSavingNews } = useLmsNewsSave();

    const { data: fetchedNews, isFetching } = useGetLmsNewsByIdQuery(currentId, { skip: !visible || !currentId, refetchOnMountOrArgChange: true });
    const news = fetchedNews && fetchedNews.id === currentId ? fetchedNews : null;

    useEffect(() => {
        if (!visible) return;
        setCurrentId(newsId || null);
        setForm(EMPTY_FORM);
        setPhoto(EMPTY_PHOTO_STATE);
        setSubmitted(false);
    }, [visible, newsId]);

    useEffect(() => {
        if (!news) return;
        setForm({
            title: news.title || '',
            shortDescription: news.shortDescription || '',
            detailDescription: news.detailDescription || '',
            publishDate: parseUtcDate(news.publishDate)
        });
    }, [news]);

    const isEdit = !!currentId;
    const isTrainingNews = isEdit && news && !isAnnouncement(news);
    const titleRequired = !isTrainingNews;
    const titleInvalid = submitted && titleRequired && !form.title.trim();

    const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

    const handleSave = async () => {
        setSubmitted(true);
        if (titleRequired && !form.title.trim()) {
            toastService.warn(th('lmsNews.form.titleRequired'), th('common.warning'));
            return;
        }

        const fields = {
            title: form.title.trim() || null,
            shortDescription: form.shortDescription.trim(),
            detailDescription: form.detailDescription.trim(),
            publishDate: form.publishDate ? toApiUtcDate(form.publishDate) : null
        };

        try {
            const { newsId: savedId, photoFailed } = await saveNews({
                id: currentId,
                fields: isEdit ? fields : { newsType: NEWS_TYPE.ANNOUNCEMENT, ...fields },
                photo
            });
            onSaved?.();
            if (photoFailed) {
                toastService.warn(th('lmsNews.form.photoUploadFailed'), th('common.warning'), 6000);
                setCurrentId(savedId);
                setPhoto(EMPTY_PHOTO_STATE);
                return;
            }
            toastService.success(th('lmsNews.form.saveSuccess'), th('common.success'));
            onHide();
        } catch (err) {
            toastService.error(formatErrorMessage(err) || th('lmsNews.form.saveError'), th('common.error'));
        }
    };

    const header = isEdit ? (isTrainingNews ? th('lmsNews.form.editTrainingTitle') : th('lmsNews.form.editTitle')) : th('lmsNews.form.addTitle');

    const footer = (
        <div>
            <Button label={th('common.cancel')} icon="pi pi-times" outlined type="button" onClick={onHide} disabled={isSavingNews} />
            {!readOnly && <Button label={th('common.save')} icon="pi pi-check" type="button" onClick={handleSave} loading={isSavingNews} disabled={isEdit && isFetching && !news} />}
        </div>
    );

    return (
        <Dialog visible={visible} onHide={onHide} header={header} footer={footer} modal style={{ width: '50rem' }} breakpoints={{ '960px': '90vw' }} className="p-fluid">
            {isEdit && isFetching && !news ? (
                <div className="flex justify-content-center p-5">
                    <ProgressSpinner style={{ width: '2.5rem', height: '2.5rem' }} strokeWidth="4" />
                </div>
            ) : (
                <div className="grid">
                    {isTrainingNews && (
                        <div className="col-12">
                            <div className="p-3 border-round-lg" style={{ backgroundColor: '#F9FAFB', border: '1px solid #EAECF0' }}>
                                <div className="flex align-items-center gap-2 mb-2 text-sm text-color-secondary">
                                    <i className="pi pi-lock" />
                                    {th('lmsNews.form.trainingInfoReadonly')}
                                </div>
                                <div className="grid">
                                    <ReadonlyItem label={th('lmsNews.columns.training')} value={news.trainingName} />
                                    <ReadonlyItem label={th('lmsNews.fields.date')} value={formatTrainingDateRange(news)} />
                                    <ReadonlyItem label={th('lmsNews.fields.location')} value={news.planningLocationText} />
                                    <ReadonlyItem label={th('lmsNews.fields.capacity')} value={news.trainingCapacity != null ? String(news.trainingCapacity) : ''} />
                                    <ReadonlyItem label={th('lmsNews.fields.trainingType')} value={news.trainingTypeText} />
                                    <ReadonlyItem label={th('lmsNews.fields.price')} value={formatFee(news, th('lmsNews.free'))} />
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="col-12">
                        <div className="field">
                            <label htmlFor="newsTitle">
                                {th('lmsNews.form.fields.title')} {titleRequired && <span className="text-red-500">*</span>}
                            </label>
                            <InputText
                                id="newsTitle"
                                value={form.title}
                                maxLength={NEWS_TITLE_MAX}
                                onChange={(e) => setField('title', e.target.value)}
                                placeholder={isTrainingNews ? news.trainingName || '' : ''}
                                className={titleInvalid ? 'p-invalid' : ''}
                            />
                            {isTrainingNews && <small className="text-color-secondary">{th('lmsNews.form.titleTrainingHint')}</small>}
                        </div>
                    </div>

                    <div className="col-12 md:col-6">
                        <div className="field">
                            <label>{th('lmsNews.form.fields.photo')}</label>
                            <NewsPhotoPicker news={isEdit ? news : null} value={photo} onChange={setPhoto} disabled={isSavingNews || readOnly} />
                        </div>
                    </div>

                    <div className="col-12 md:col-6">
                        <div className="field">
                            <label htmlFor="newsPublishDate">{th('lmsNews.form.fields.publishDate')}</label>
                            <Calendar id="newsPublishDate" value={form.publishDate} onChange={(e) => setField('publishDate', e.value || null)} showTime hourFormat="24" dateFormat="dd.mm.yy" showIcon showButtonBar appendTo={document.body} />
                            <small className="text-color-secondary">{th('lmsNews.form.publishDateHint')}</small>
                        </div>
                    </div>

                    <div className="col-12">
                        <div className="field">
                            <label htmlFor="newsShortDescription">{th('lmsNews.form.fields.shortDescription')}</label>
                            <InputTextarea id="newsShortDescription" value={form.shortDescription} maxLength={NEWS_SHORT_DESCRIPTION_MAX} rows={3} autoResize onChange={(e) => setField('shortDescription', e.target.value)} />
                            <small className="text-color-secondary flex justify-content-end">
                                {form.shortDescription.length} / {NEWS_SHORT_DESCRIPTION_MAX}
                            </small>
                        </div>
                    </div>

                    <div className="col-12">
                        <div className="field mb-0">
                            <label htmlFor="newsDetailDescription">{th('lmsNews.form.fields.detailDescription')}</label>
                            <InputTextarea id="newsDetailDescription" value={form.detailDescription} rows={6} autoResize onChange={(e) => setField('detailDescription', e.target.value)} />
                        </div>
                    </div>
                </div>
            )}
        </Dialog>
    );
};

export default LmsNewsFormDialog;
